import SearchInput from "components/input/SearchInput";
import { Button } from "components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { ArrowDownUp, X } from "lucide-react";
import { useTempoChatListQuery, useTempoChatUnreadCounts } from "query/tempo-chat.query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface TempoChatsFilterBarProps {
  searchValue: string | null;
  onSearchChange: (value: string | null) => void;
  isUnreadOnly: boolean;
  onUnreadOnlyClick: () => void;
  targetLanguageFilterValue?: string;
  onTargetLanguageFilterValueChange: (value?: string) => void;
  chatsFilterValue: string;
  onChatsFilterValueChange: (value: string) => void;
  customerFilterValue?: string;
  onCustomerFilterValueChange: (value?: string) => void;
  sortValue: string;
  onSortValueChange: (value: string) => void;
  sortDirection: "asc" | "desc";
  onSortDirectionChange: (value: "asc" | "desc") => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}
export const TempoChatsFilterBar = ({
  searchValue,
  onSearchChange,
  isUnreadOnly,
  onUnreadOnlyClick,
  targetLanguageFilterValue,
  onTargetLanguageFilterValueChange,
  chatsFilterValue,
  onChatsFilterValueChange,
  customerFilterValue,
  onCustomerFilterValueChange,
  sortValue,
  onSortValueChange,
  sortDirection,
  onSortDirectionChange,
  hasActiveFilters = false,
  onClearFilters,
}: TempoChatsFilterBarProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "messaging.tempo-chat-list-page.filters",
  });
  const { data } = useTempoChatListQuery();
  const { data: tempoChatUnreadCounts } = useTempoChatUnreadCounts();

  const [targetLanguageOptions, setTargetLanguageOptions] = useState<string[]>([]);
  const [customerOptions, setCustomerOptions] = useState<string[]>([]);

  useEffect(() => {
    if (data?.tempoChats) {
      const uniqueTargetLanguages = [...new Set(data.tempoChats.map((chat) => chat.targetLanguage))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      const uniqueCustomers = [...new Set(data.tempoChats.map((chat) => chat.customerName))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setTargetLanguageOptions(uniqueTargetLanguages);
      setCustomerOptions(uniqueCustomers);
    } else {
      setTargetLanguageOptions([]);
      setCustomerOptions([]);
    }
  }, [data]);

  const totalUnreadCount = useMemo(() => {
    if (!tempoChatUnreadCounts) return 0;
    return Object.values(tempoChatUnreadCounts).reduce((a, b) => a + b, 0);
  }, [tempoChatUnreadCounts]);

  return (
    <div className="text-color-base bg-color-base mb-4 flex w-full flex-wrap gap-4 rounded-md border border-slate-200 p-4 dark:border-slate-800 lg:justify-between">
      <div className="flex flex-col flex-wrap items-start gap-4 sm:flex-row">
        <div>
          <SearchInput
            onSearch={(value) => onSearchChange(value)}
            term={searchValue}
            placeholder={t("search.placeholder")}
            className="min-w-96"
          />
        </div>

        <Button
          data-test="btn-toggle-unread-only"
          variant={isUnreadOnly ? "primary" : "outline"}
          onClick={onUnreadOnlyClick}
          className="flex flex-nowrap gap-2 text-nowrap text-sm"
        >
          <span>{t("unread-btn-label")}</span>
          <span className="h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-2 py-1 text-xs font-bold leading-none text-white">
            {totalUnreadCount}
          </span>
        </Button>

        {targetLanguageOptions.length > 1 && (
          <Select value={targetLanguageFilterValue} onValueChange={onTargetLanguageFilterValueChange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t("target-language.placeholder")} />
            </SelectTrigger>
            <SelectContent className="min-w-48">
              <SelectItem value={"all-languages"}>{t("target-language.all-languages")}</SelectItem>
              {targetLanguageOptions.map((language) => (
                <SelectItem key={language} value={language}>
                  {language}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={chatsFilterValue} onValueChange={onChatsFilterValueChange}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="min-w-48">
            <SelectItem value={"all-chats"}>{t("chats.all-chats")}</SelectItem>
            <SelectItem value={"active-chats"}>{t("chats.active-chats")}</SelectItem>
            <SelectItem value={"closed-chats"}>{t("chats.closed-chats")}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={customerFilterValue} onValueChange={onCustomerFilterValueChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("customer.placeholder")} />
          </SelectTrigger>
          <SelectContent className="min-w-48">
            <SelectItem value={"all-customers"}>{t("customer.all-customers")}</SelectItem>
            {customerOptions.map((customer) => (
              <SelectItem key={customer} value={customer}>
                {customer}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && onClearFilters && (
          <Button
            size="sm"
            data-test="btn-clear-filters"
            variant="outline"
            onClick={onClearFilters}
            className="flex flex-nowrap gap-2 text-nowrap text-sm"
          >
            <X className="size-5 shrink-0" />
            {t("clear-filters")}
          </Button>
        )}
      </div>
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <Select value={sortValue} onValueChange={onSortValueChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("")} />
          </SelectTrigger>
          <SelectContent className="min-w-48">
            <SelectItem value={"last-message"}>{t("sort.last-message")}</SelectItem>
            <SelectItem value={"customer"}>{t("sort.customer")}</SelectItem>
            <SelectItem value={"project-name"}>{t("sort.project-name")}</SelectItem>
            {targetLanguageOptions.length > 1 && (
              <SelectItem value={"target-language"}>{t("sort.target-language")}</SelectItem>
            )}
            <SelectItem value={"task-code"}>{t("sort.task-code")}</SelectItem>
          </SelectContent>
        </Select>

        <Button
          data-test="btn-toggle-sort-direction"
          variant={"outline"}
          onClick={() => onSortDirectionChange(sortDirection === "asc" ? "desc" : "asc")}
          className="flex flex-nowrap gap-2 text-nowrap text-sm"
        >
          <ArrowDownUp className="size-5 shrink-0" />
          <span>{t(`sort-direction.${sortDirection}`)}</span>
        </Button>
      </div>
    </div>
  );
};
